// The Full widget: a large quote page for a dedicated page on the installer's
// site. Steps: Location → EPC → Property → Grant → Estimate → Contact, with a
// sidebar showing progress and what's been gathered so far.
//
// Same rules as the small widget (render.ts): framework-free, rendered into a
// shadow root, runs unchanged on websites (real API) and in the console
// preview (mock API). Branding comes in through the same CSS variables.

import { ensureGoogleFont } from "./fonts";
import { METHOD_STATEMENT } from "./method";
import { FULL_CSS } from "./fullStyles";
import { WIDGET_CSS, cssVars } from "./styles";
import type {
  Address,
  EpcCertificate,
  FullProperty,
  FullStep,
  GrantAnswers,
  GrantResult,
  PropertyDetails,
  PropertyLookup,
  Quote,
  WidgetApi,
  WidgetBranding,
} from "./types";
import { FULL_STEPS } from "./types";

export interface FullMountOptions {
  branding: WidgetBranding;
  api: WidgetApi;
  contained?: boolean;
  highlight?: boolean;
  startOpen?: boolean;
  onStep?: (step: FullStep) => void;
  /** Offer "Get my detailed quote" after the estimate (Quote workflow add-on, session.quotes) */
  quotes?: boolean;
}

export interface FullHandle {
  update(branding: WidgetBranding): void;
  goTo(step: FullStep | string): Promise<void>;
  destroy(): void;
}

// ─── Choices ─────────────────────────────────────────────────────────────────

type Opt<T extends string> = [T, string];
const KINDS: Opt<FullProperty["kind"]>[] = [
  ["house", "House"],
  ["bungalow", "Bungalow"],
  ["flat", "Flat"],
  ["maisonette", "Maisonette"],
];
const FORMS: Opt<FullProperty["builtForm"]>[] = [
  ["detached", "Detached"],
  ["semi-detached", "Semi-detached"],
  ["end-terrace", "End terrace"],
  ["mid-terrace", "Mid terrace"],
  ["enclosed-end-terrace", "Enclosed end terrace"],
  ["enclosed-mid-terrace", "Enclosed mid terrace"],
];
const FUELS: Opt<FullProperty["fuel"]>[] = [
  ["gas", "Mains gas"],
  ["oil", "Oil"],
  ["lpg", "LPG"],
  ["electric", "Electricity"],
  ["other", "Other (e.g. coal, wood)"],
];
const HEATING_SYSTEMS: Opt<FullProperty["heatingSystem"]>[] = [
  ["boiler-radiators", "Boiler + radiators"],
  ["boiler-underfloor", "Boiler + underfloor"],
  ["storage-heaters", "Storage heaters"],
  ["room-heaters", "Room heaters"],
  ["heat-pump", "Heat pump"],
  ["other", "Other"],
];
const HOT_WATER: Opt<FullProperty["hotWater"]>[] = [
  ["main-system", "From main system"],
  ["immersion", "Electric immersion"],
  ["other", "Other"],
];

// Construction years per age band: RdSAP 10 Specification (June 2025) Table 1
const AGE_BANDS: Record<"England/Wales" | "Scotland" | "Northern Ireland", [string, string][]> = {
  "England/Wales": [
    ["A", "Before 1900"], ["B", "1900–1929"], ["C", "1930–1949"], ["D", "1950–1966"], ["E", "1967–1975"], ["F", "1976–1982"], ["G", "1983–1990"],
    ["H", "1991–1995"], ["I", "1996–2002"], ["J", "2003–2006"], ["K", "2007–2011"], ["L", "2012–2022"], ["M", "2023 onwards"],
  ],
  Scotland: [
    ["A", "Before 1919"], ["B", "1919–1929"], ["C", "1930–1949"], ["D", "1950–1964"], ["E", "1965–1975"], ["F", "1976–1983"], ["G", "1984–1991"],
    ["H", "1992–1998"], ["I", "1999–2002"], ["J", "2003–2007"], ["K", "2008–2011"], ["L", "2012–2023"], ["M", "2024 onwards"],
  ],
  "Northern Ireland": [
    ["A", "Before 1919"], ["B", "1919–1929"], ["C", "1930–1949"], ["D", "1950–1973"], ["E", "1974–1977"], ["F", "1978–1985"], ["G", "1986–1991"],
    ["H", "1992–1999"], ["I", "2000–2006"], ["K", "2007–2013"], ["L", "2014–2022"], ["M", "2023 onwards"],
  ],
};

// EPC rating chart: GOV.UK certificate bands and colours
const EPC_BANDS: [string, string, string][] = [
  ["A", "92+", "#008054"],
  ["B", "81–91", "#19b459"],
  ["C", "69–80", "#8dce46"],
  ["D", "55–68", "#ffd500"],
  ["E", "39–54", "#fcaa65"],
  ["F", "21–38", "#ef8023"],
  ["G", "1–20", "#e9153b"],
];

const STEP_NAMES: Record<FullStep, string> = {
  location: "Location",
  epc: "EPC",
  property: "Property",
  grant: "Grant",
  estimate: "Estimate",
  contact: "Your details",
  done: "Done",
};

const SQFT_PER_M2 = 10.7639;
const POSTCODE_RE = /^[A-Z]{1,2}\d[A-Z\d]?\s*\d[A-Z]{2}$/i;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function esc(s: string | number): string {
  return String(s).replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c]!);
}
const gbp = (n: number) => `£${Math.round(n).toLocaleString("en-GB")}`;
const label = <T extends string>(opts: Opt<T>[], v: T | undefined) => opts.find(([k]) => k === v)?.[1] ?? "";

// Line icons (inline SVG: no requests from customers' sites)
const svg = (d: string) =>
  `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">${d}</svg>`;
const ICONS: Record<string, string> = {
  location: svg('<path d="M12 21s-7-6.2-7-11.5A7 7 0 0 1 19 9.5C19 14.8 12 21 12 21z"/><circle cx="12" cy="9.5" r="2.5"/>'),
  epc: svg('<path d="M14 3H6a1 1 0 0 0-1 1v16a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V8z"/><path d="M14 3v5h5M9 13h6M9 17h6"/>'),
  property: svg('<path d="M3 10.5 12 3l9 7.5V21a1 1 0 0 1-1 1h-5v-7H9v7H4a1 1 0 0 1-1-1z"/>'),
  grant: svg('<circle cx="12" cy="12" r="9"/><path d="m8 12.5 2.5 2.5L16 9.5"/>'),
  estimate: svg('<path d="M4 20V10M10 20V4M16 20v-7M22 20H2"/>'),
  contact: svg('<rect x="3" y="5" width="18" height="14" rx="2"/><path d="m3 7 9 6 9-6"/>'),
  done: svg('<path d="M5 12.5 10 17l9-10"/>'),
  info: svg('<circle cx="12" cy="12" r="9"/><path d="M12 11v5M12 8h.01"/>'),
  pass: svg('<circle cx="12" cy="12" r="9"/><path d="m8 12.5 2.5 2.5L16 9.5"/>'),
  fail: svg('<circle cx="12" cy="12" r="9"/><path d="m9 9 6 6M15 9l-6 6"/>'),
  external: svg('<path d="M14 4h6v6M20 4l-9 9M18 14v5a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1h5"/>'),
};
const XENSCOPE_GLYPH =
  '<svg viewBox="0 0 10 18" aria-hidden="true"><path d="M0 0 6.2 3.6Q10 5.8 10 9.2V9.6H0Z" fill="#E1EE2B"/><rect y="11" width="10" height="7" rx="2.4" fill="#014852"/></svg>';

/** The small widget's three answers, derived from the Full answers (the API and emails use both). */
export function toDetails(p: FullProperty): PropertyDetails {
  const propertyType: PropertyDetails["propertyType"] =
    p.kind === "flat" || p.kind === "maisonette"
      ? "flat"
      : p.kind === "bungalow"
        ? "bungalow"
        : p.builtForm === "detached"
          ? "detached"
          : p.builtForm === "semi-detached"
            ? "semi-detached"
            : "terraced";
  return { propertyType, bedrooms: Math.max(1, Math.min(5, p.bedrooms)), heating: p.fuel };
}

// Starting answers when there's no EPC value (the homeowner confirms each)
const BLANK: FullProperty = {
  kind: "house",
  builtForm: "semi-detached",
  ageBand: "",
  storeys: 2,
  roomInRoof: false,
  floorArea: 0,
  fuel: "gas",
  heatingSystem: "boiler-radiators",
  hotWater: "main-system",
  bedrooms: 3,
  bathrooms: 1,
  radiators: 8,
  wallsInsulated: false,
  roofInsulated: false,
  floorInsulated: false,
  mainsGas: null,
};

interface State {
  step: FullStep;
  reached: number; // furthest step index reached (sidebar links back to these)
  open: boolean;
  busy: boolean;
  error: string | null;
  postcode: string;
  addresses: Address[];
  address: Address | null;
  lookup: PropertyLookup | null | undefined;
  property: FullProperty;
  fromEpc: Partial<FullProperty>;
  sqft: boolean;
  grantAnswers: Partial<GrantAnswers>;
  grantResult: GrantResult | null;
  includeGrant: boolean | null;
  quote: Quote | null;
  /** The homeowner chose "Get my detailed quote" (not a callback) */
  wantsQuote: boolean;
  /** The quote page, once the quote has been sent */
  quoteUrl: string | null;
  name: string;
  email: string;
  phone: string;
  consent: boolean;
}

export function mountFullWidget(host: HTMLElement, opts: FullMountOptions): FullHandle {
  let branding = opts.branding;
  const api = opts.api;

  const shadow = host.shadowRoot ?? host.attachShadow({ mode: "open" });
  shadow.innerHTML = "";
  const style = document.createElement("style");
  style.textContent = WIDGET_CSS + FULL_CSS;
  const root = document.createElement("div");
  shadow.append(style, root);

  const state: State = {
    step: "location",
    reached: 0,
    open: opts.startOpen ?? false,
    busy: false,
    error: null,
    postcode: "",
    addresses: [],
    address: null,
    lookup: undefined,
    property: { ...BLANK },
    fromEpc: {},
    sqft: false,
    grantAnswers: {},
    grantResult: null,
    includeGrant: null,
    quote: null,
    wantsQuote: false,
    quoteUrl: null,
    name: "",
    email: "",
    phone: "",
    consent: false,
  };

  // ─── Rendering ─────────────────────────────────────────────────────────────

  function render() {
    ensureGoogleFont(branding.font.family);
    for (const [k, v] of Object.entries(cssVars(branding))) root.style.setProperty(k, v);
    root.className = `xq-root${opts.contained ? " xq-contained" : ""}${opts.highlight ? " xq-highlight" : ""}`;
    const scroll = root.querySelector(".xq-list")?.scrollTop ?? 0;

    const l = branding.layout;
    if (l.mode === "floating") {
      // Full as a floating widget: the launcher opens the page as a large dialog
      root.innerHTML = state.open
        ? `<div class="xqf-overlay" role="dialog" aria-modal="true">${page()}</div>`
        : `<div class="xq-floating xq-pos-${l.position}"><button type="button" class="xq-launcher" data-action="open">${ICONS.property}${esc(l.launcherLabel)}</button></div>`;
    } else {
      root.innerHTML = page();
    }
    const list = root.querySelector(".xq-list");
    if (list) list.scrollTop = scroll;
    syncDisabled();
  }

  function page(): string {
    const logo = branding.logoUrl && /^https:\/\//.test(branding.logoUrl) ? `<img class="xq-logo" src="${esc(branding.logoUrl)}" alt="">` : "";
    const close = branding.layout.mode === "floating" ? `<button type="button" class="xq-close" data-action="close" aria-label="Close">×</button>` : "";
    return `<div class="xqf-wrap"><div class="xqf" role="region" aria-label="${esc(branding.copy.title)}">
      <main class="xqf-main">
        <div class="xqf-card">
          ${stepHtml()}
          ${state.error ? `<p class="xq-error" role="alert">${esc(state.error)}</p>` : ""}
        </div>
      </main>
      <aside class="xqf-side">
        <div class="xqf-side-card">
          <div class="xqf-side-head">${logo || `<strong>${esc(branding.copy.title)}</strong>`}${close}</div>
          <div class="xq-progress" aria-hidden="true"><span style="width:${Math.round((FULL_STEPS.indexOf(state.step) / (FULL_STEPS.length - 1)) * 100)}%"></span></div>
          <ol class="xqf-steps">${FULL_STEPS.filter((s) => s !== "done").map(sideItem).join("")}</ol>
        </div>
        <div class="xq-foot">Powered by <a href="https://xenscope.com" target="_blank" rel="noopener">${XENSCOPE_GLYPH}xenscope</a></div>
      </aside>
    </div></div>`;
  }

  /** One sidebar entry: icon, name and a summary of what that step gathered. */
  function sideItem(s: FullStep): string {
    const i = FULL_STEPS.indexOf(s);
    const cls = s === state.step ? "is-current" : i <= state.reached ? "is-done" : "is-todo";
    const summary = i <= state.reached && s !== state.step ? stepSummary(s) : s === state.step ? "In progress" : "";
    const inner = `<span class="xqf-step-icon">${ICONS[s]}</span><span><span class="xqf-step-name">${STEP_NAMES[s]}</span>${summary ? `<span class="xqf-step-sum">${esc(summary)}</span>` : ""}</span>`;
    // Earlier steps are links back; later ones aren't clickable yet
    return `<li class="${cls}">${i <= state.reached && s !== state.step && state.step !== "done" ? `<button type="button" data-action="goto" data-step="${s}">${inner}</button>` : `<div>${inner}</div>`}</li>`;
  }

  function stepSummary(s: FullStep): string {
    const p = state.property;
    const cert = state.lookup?.full?.certificate;
    switch (s) {
      case "location":
        return state.address?.line ?? "";
      case "epc":
        return cert?.current.band ? `Rating ${cert.current.band}${cert.current.score ? ` (${cert.current.score})` : ""}` : "No certificate";
      case "property":
        return [label(KINDS, p.kind), p.kind === "flat" || p.kind === "maisonette" ? "" : label(FORMS, p.builtForm), p.floorArea ? `${p.floorArea} m²` : ""].filter(Boolean).join(" | ");
      case "grant":
        return state.includeGrant && state.grantResult?.grant ? `${gbp(state.grantResult.grant)} included` : "Not included";
      case "estimate":
        return state.quote && !state.quote.tooBig && !state.quote.noEstimate ? `${gbp(state.quote.low)} – ${gbp(state.quote.high)}` : state.quote ? "Survey needed" : "";
      default:
        return "";
    }
  }

  /** The main card for the current step. */
  function stepHtml(): string {
    const c = branding.copy;
    switch (state.step) {
      case "location":
        return `<header class="xqf-head"><h2 class="xq-title">${esc(c.title)}</h2><p class="xq-sub">${esc(c.subtitle)}</p></header>
          <form data-form="postcode" novalidate class="xqf-section">
            <label class="xq-label" for="xqf-postcode">Postcode</label>
            <div class="xq-row">
              <input class="xq-input" id="xqf-postcode" data-model="postcode" value="${esc(state.postcode)}" placeholder="e.g. SW1A 1AA" autocomplete="postal-code">
              <button type="submit" class="xq-btn" data-needs="postcode">${state.busy && !state.addresses.length ? "Searching…" : esc(c.buttonText)}</button>
            </div>
          </form>
          ${
            state.addresses.length
              ? `<div class="xqf-section"><span class="xq-label">Select your address</span><div class="xq-list">${state.addresses
                  .map((a) => `<button type="button" class="xq-option" data-action="pick-address" data-id="${esc(a.id)}" aria-pressed="${state.address?.id === a.id}">${esc(a.line)}</button>`)
                  .join("")}</div></div>`
              : ""
          }
          ${footer("to-epc", "address", state.busy && state.addresses.length ? "Checking your home…" : "Next step")}`;

      case "epc": {
        const cert = state.lookup?.full?.certificate;
        return `<header class="xqf-head"><h2 class="xq-title">Your energy certificate</h2><p class="xq-sub">${
          cert ? "We found your home's EPC on the government register. We'll use it to fill in the next page." : "We couldn't find an EPC for this address. That's fine: tell us about your home on the next page."
        }</p></header>
          ${cert ? epcCard(cert) : ""}
          ${footer("to-property", null, "Next step")}`;
      }

      case "property":
        return `<header class="xqf-head"><h2 class="xq-title">Property</h2><p class="xq-sub">Please review the details below and update anything that's not right.${
          Object.keys(state.fromEpc).length ? ' Answers marked <span class="xqf-epc-tag">EPC</span> come from your energy certificate.' : ""
        }</p></header>
          <p class="xqf-missing-note" data-missing-note ${missingFields().length ? "" : "hidden"}>Please answer the highlighted ${missingFields().length === 1 ? "question" : "questions"} to continue.</p>
          <div class="xqf-rows">${propertyRows()}</div>
          ${footer("to-grant", "property", "Next step")}`;

      case "grant":
        return `<header class="xqf-head"><h2 class="xq-title">Boiler Upgrade Scheme grant</h2><p class="xq-sub">A government grant towards a heat pump. A few questions to see if your home is likely to qualify.</p></header>
          <div class="xqf-rows">${grantQuestions()}</div>
          ${grantResultHtml()}
          ${footer("to-estimate", "grant", state.busy ? "Calculating…" : "See my estimate")}`;

      case "estimate":
        return estimateHtml();

      case "contact":
        return `<header class="xqf-head"><h2 class="xq-title">${state.wantsQuote ? "Get your detailed quote" : "Where should we send your quote?"}</h2><p class="xq-sub">${
          state.wantsQuote
            ? "We'll show it to you now and email you a copy: the system we recommend, fixed prices and running costs. It's subject to a home survey."
            : "We'll confirm your price and arrange a home survey."
        }</p></header>
          <form data-form="contact" novalidate class="xqf-section xqf-contact">
            <div class="xq-field"><label class="xq-label" for="xqf-name">Name</label><input class="xq-input" id="xqf-name" data-model="name" value="${esc(state.name)}" autocomplete="name"></div>
            <div class="xq-field"><label class="xq-label" for="xqf-email">Email</label><input class="xq-input" id="xqf-email" type="email" data-model="email" value="${esc(state.email)}" autocomplete="email"></div>
            <div class="xq-field"><label class="xq-label" for="xqf-phone">Phone (optional)</label><input class="xq-input" id="xqf-phone" type="tel" data-model="phone" value="${esc(state.phone)}" autocomplete="tel"></div>
            <label class="xq-consent"><input type="checkbox" data-model="consent" ${state.consent ? "checked" : ""}><span>${esc(c.consentText)}</span></label>
            <div class="xqf-foot"><button type="button" class="xq-link" data-action="back">Back</button>
              <button type="submit" class="xq-btn" data-needs="contact">${state.busy ? (state.wantsQuote ? "Preparing your quote…" : "Sending…") : state.wantsQuote ? "Get my quote" : "Send my quote"}</button></div>
            ${state.wantsQuote ? '<p class="xq-hint">Your installer keeps your name and contact details with your quote.</p>' : ""}
          </form>`;

      case "done":
        if (state.quoteUrl) {
          return `<div class="xq-done"><div class="xq-done-icon">${ICONS.done}</div>
          <h2 class="xq-title">Your quote is ready</h2>
          <p class="xq-sub">We've also emailed it to ${esc(state.email.trim())}. Compare the options, add any extras and accept it online. It's subject to a home survey.</p>
          <a class="xq-btn" href="${esc(state.quoteUrl)}" target="_blank" rel="noopener">View my quote</a>
          <p><button type="button" class="xq-link" data-action="restart">Start again</button></p></div>`;
        }
        return `<div class="xq-done"><div class="xq-done-icon">${ICONS.done}</div>
          <h2 class="xq-title">${esc(c.thankYouTitle)}</h2><p class="xq-sub">${esc(c.thankYouText)}</p>
          <button type="button" class="xq-link" data-action="restart">Start again</button></div>`;
    }
  }

  /** Back link + primary "next" button at the foot of a step. */
  function footer(action: string, needs: string | null, text: string): string {
    const back = state.step === "location" ? "<span></span>" : `<button type="button" class="xq-link" data-action="back">Back</button>`;
    return `<div class="xqf-foot">${back}<button type="button" class="xq-btn" data-action="${action}"${needs ? ` data-needs="${needs}"` : ""}>${esc(text)}</button></div>`;
  }

  /** The EPC card: address + rating, certificate number, A–G chart, GOV.UK link. */
  function epcCard(cert: EpcCertificate): string {
    const band = cert.current.band ?? "?";
    const arrow = (which: "current" | "potential", b: string) => {
      const r = cert[which];
      return r.band === b ? `<span class="xqf-arrow" style="--c:${EPC_BANDS.find(([x]) => x === b)?.[2]}">${r.score ?? ""} ${b}</span>` : "";
    };
    const rows = EPC_BANDS.map(
      ([b, range, colour], i) => `<tr>
        <td class="xqf-epc-range" style="background:color-mix(in srgb, ${colour} 60%, white)">${range}</td>
        <td class="xqf-epc-barcell"><span class="xqf-epc-bar" style="width:${34 + i * 11}%;background:${colour}">${b}</span></td>
        <td class="xqf-epc-col">${arrow("current", b)}</td>
        <td class="xqf-epc-col">${arrow("potential", b)}</td></tr>`,
    ).join("");
    const link = cert.number ? `https://find-energy-certificate.service.gov.uk/energy-certificate/${encodeURIComponent(cert.number)}` : null;
    return `<div class="xqf-epc">
      <div class="xqf-epc-top">
        <div class="xqf-epc-addr">${cert.addressLines.map(esc).join("<br>")}</div>
        <div class="xqf-epc-rating"><span>Energy rating</span><strong>${esc(band)}</strong></div>
      </div>
      ${cert.number ? `<div class="xqf-epc-num"><span>Certificate number</span><strong>${esc(cert.number)}</strong>${cert.lodgedDate ? `<span>Lodged ${esc(new Date(cert.lodgedDate).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" }))}</span>` : ""}</div>` : ""}
      <table class="xqf-epc-chart"><thead><tr><th>Score</th><th>Energy rating</th><th>Current</th><th>Potential</th></tr></thead><tbody>${rows}</tbody></table>
      ${link ? `<a class="xqf-btn-outline" href="${link}" target="_blank" rel="noopener">Certificate on GOV.UK ${ICONS.external}</a>` : ""}
    </div>`;
  }

  /** The Property page: one row per question with its control on the right. */
  function propertyRows(): string {
    const p = state.property;
    const epc = state.fromEpc;
    const isFlat = p.kind === "flat" || p.kind === "maisonette";
    const tag = (f: keyof FullProperty) => (epc[f] !== undefined && epc[f] === p[f] ? ' <span class="xqf-epc-tag" title="From your energy certificate">EPC</span>' : "");
    // Required answers still missing glow; the first also carries the pulsing star
    const missing = missingFields();
    const row = (f: keyof FullProperty, title: string, sub: string, control: string) =>
      `<div class="xqf-row${missing.includes(f) ? " is-missing" : ""}" data-row="${f}">${needStar(f === missing[0])}<div class="xqf-row-text"><span class="xqf-row-title">${title}${tag(f)}</span>${sub ? `<span class="xqf-row-sub">${sub}</span>` : ""}</div><div class="xqf-row-control">${control}</div></div>`;
    const select = <T extends string>(f: keyof FullProperty, options: Opt<T>[], placeholder?: string) =>
      `<select class="xq-input xqf-select" data-field="${f}">${placeholder ? `<option value="" ${p[f] ? "" : "selected"} disabled>${placeholder}</option>` : ""}${options
        .map(([v, t]) => `<option value="${v}" ${String(p[f]) === v ? "selected" : ""}>${t}</option>`)
        .join("")}</select>`;
    const stepper = (f: keyof FullProperty, min: number, max: number, value: number, unit = "") =>
      `<div class="xqf-stepper"><button type="button" data-action="step" data-field="${f}" data-delta="-1" aria-label="Fewer" ${value <= min ? "disabled" : ""}>−</button>
        <input type="number" inputmode="numeric" data-field="${f}" value="${value}" min="${min}" max="${max}" aria-label="${f}">${unit}
        <button type="button" data-action="step" data-field="${f}" data-delta="1" aria-label="More" ${value >= max ? "disabled" : ""}>+</button></div>`;
    const yesNo = (f: keyof FullProperty) =>
      `<div class="xqf-yesno" role="group"><button type="button" data-action="yesno" data-field="${f}" data-value="true" aria-pressed="${p[f] === true}">Yes</button><button type="button" data-action="yesno" data-field="${f}" data-value="false" aria-pressed="${p[f] === false}">No</button></div>`;
    const country = state.lookup?.full?.country;
    const bands = AGE_BANDS[country === "Scotland" ? "Scotland" : country === "Northern Ireland" ? "Northern Ireland" : "England/Wales"];
    const area = state.sqft ? Math.round(p.floorArea * SQFT_PER_M2) : p.floorArea;

    return [
      row("kind", "Property type", "", select("kind", KINDS)),
      isFlat ? "" : row("builtForm", "House type", "", select("builtForm", FORMS)),
      row("ageBand", "When was it built?", "Roughly is fine", select("ageBand", bands as Opt<string>[], "Choose…")),
      row("storeys", isFlat ? "Floors in your home" : "Number of floors", "Not counting a loft room", stepper("storeys", 1, 5, p.storeys)),
      isFlat ? "" : row("roomInRoof", "Room in the roof", "A converted loft you live in", yesNo("roomInRoof")),
      row(
        "floorArea",
        `Total floor area (${state.sqft ? "ft²" : "m²"})`,
        `<button type="button" class="xq-link" data-action="units">${state.sqft ? "Use square metres" : "Prefer square feet?"}</button>`,
        stepper("floorArea", 0, state.sqft ? 10000 : 1000, area),
      ),
      row("fuel", "Main fuel type", "", select("fuel", FUELS)),
      row("heatingSystem", "Heating", "", select("heatingSystem", HEATING_SYSTEMS)),
      row("hotWater", "Hot water", "", select("hotWater", HOT_WATER)),
      row("bedrooms", "Bedrooms", "", stepper("bedrooms", 1, 10, p.bedrooms)),
      row("bathrooms", "Bathrooms", "", stepper("bathrooms", 1, 6, p.bathrooms)),
      row("radiators", "Radiators", "Set to zero if you have underfloor heating", stepper("radiators", 0, 40, p.radiators)),
      p.roofInsulated === null ? "" : row("roofInsulated", "Loft or roof insulated", "Insulation between or over the joists, or in the roof slope", yesNo("roofInsulated")),
      row("wallsInsulated", "Walls insulated", "Cavity fill, or internal or external wall insulation", yesNo("wallsInsulated")),
      p.floorInsulated === null ? "" : row("floorInsulated", "Floor insulated", "", yesNo("floorInsulated")),
    ].join("");
  }

  /** Grant questions (yes/no), including mains gas for oil/LPG homes. */
  function grantQuestions(): string {
    const a = state.grantAnswers;
    const q = (k: keyof GrantAnswers, title: string, sub = "") =>
      `<div class="xqf-row"><div class="xqf-row-text"><span class="xqf-row-title">${title}</span>${sub ? `<span class="xqf-row-sub">${sub}</span>` : ""}</div>
        <div class="xqf-row-control"><div class="xqf-yesno" role="group"><button type="button" data-action="grant" data-key="${k}" data-value="true" aria-pressed="${a[k] === true}">Yes</button><button type="button" data-action="grant" data-key="${k}" data-value="false" aria-pressed="${a[k] === false}">No</button></div></div></div>`;
    const offGasQuestion = state.property.fuel === "oil" || state.property.fuel === "lpg";
    return [
      q("owner", "Do you own this home?", "Including a second home or one you let out. Not social housing or shared ownership"),
      q("newBuild", "Is it a new build?", "Built in the last couple of years and not yet lived in with its current heating"),
      q("previousFunding", "Have you had a government grant for a heat pump or biomass boiler here before?"),
      offGasQuestion ? q("mainsGas", "Is the home connected to mains gas?", "Homes with no gas connection replacing oil or LPG can get more") : "",
    ].join("");
  }

  function grantAnswered(): boolean {
    const a = state.grantAnswers;
    const needsGas = state.property.fuel === "oil" || state.property.fuel === "lpg";
    return a.owner !== undefined && a.newBuild !== undefined && a.previousFunding !== undefined && (!needsGas || a.mainsGas !== undefined);
  }

  /** The checklist and include/skip choice, once the questions are answered. */
  function grantResultHtml(): string {
    const r = state.grantResult;
    if (!grantAnswered()) return "";
    if (!r) return `<p class="xq-hint">Checking…</p>`;
    const items = r.checks
      .map((c) => `<li class="xqf-check is-${c.status}"><div><strong>${esc(c.text)}</strong><span>${esc(c.detail)}</span></div>${ICONS[c.status]}</li>`)
      .join("");
    const choice = r.eligible
      ? `<p class="xqf-choice-q">Would you like to see prices with the grant taken off?</p>
        <div class="xqf-choice" role="group">
          <button type="button" data-action="include-grant" data-value="true" aria-pressed="${state.includeGrant === true}">Include the ${gbp(r.grant)} grant</button>
          <button type="button" data-action="include-grant" data-value="false" aria-pressed="${state.includeGrant === false}">Skip the grant</button>
        </div>`
      : `<p class="xq-hint">From your answers the grant doesn't look available, so prices will be shown without it. Your installer can check.</p>`;
    return `<ul class="xqf-checks">${items}</ul><p class="xq-hint">${esc(r.note)}</p>${choice}`;
  }

  function estimateHtml(): string {
    const q = state.quote!;
    const c = branding.copy;
    const hl = q.heatLoss;
    const price = q.noEstimate
      ? `<div class="xq-quote"><div class="xq-quote-meta">We can't estimate this home's heat loss online (its energy certificate isn't the kind our published method uses, and there aren't enough similar homes nearby). We'll work out the right heat pump and price at a home survey.</div></div>`
      : q.tooBig
      ? `<div class="xq-quote"><div class="xq-quote-meta">Your home needs a larger heat pump than we can price online (more than ${esc(q.heatPumpKw)} kW). We'll recommend the right system after a survey.</div></div>`
      : `<div class="xq-quote"><div class="xq-quote-range">${gbp(q.low)} – ${gbp(q.high)}</div><div class="xq-quote-meta">${
          q.grant ? `After the ${gbp(q.grant)} Boiler Upgrade Scheme grant` : "Before any grant"
        }</div></div>`;
    const facts = `<dl class="xq-facts">
      ${hl ? `<dt>Estimated heat loss</dt><dd>${esc(hl.kw)} kW</dd>` : ""}
      ${q.noEstimate ? "" : `<dt>Heat pump</dt><dd>${q.tooBig ? `More than ${esc(q.heatPumpKw)} kW` : `${esc(q.heatPumpKw)} kW air source`}</dd>`}
      <dt>Home</dt><dd>${esc(state.address?.line ?? "")}</dd></dl>`;
    // The full calculation breakdown is no longer shown to the homeowner (it still
    // goes to the installer in the lead email, built separately in the API). We keep
    // the short "How is this estimate worked out?" method note for reassurance.
    return `<header class="xqf-head"><h2 class="xq-title">${esc(c.quoteTitle)}</h2></header>
      ${price}${facts}
      <p class="xq-hint">${esc(c.disclaimer)}</p>
      <details class="xq-method"><summary>How is this estimate worked out?</summary><p>${esc(METHOD_STATEMENT)}</p></details>
      ${
        opts.quotes && api.requestQuote && !q.noEstimate && !q.tooBig
          ? `<div class="xqf-foot"><button type="button" class="xq-link" data-action="back">Back</button>
              <span class="xqf-choices"><button type="button" class="xq-btn xq-btn-ghost" data-action="to-contact">Request a callback</button>
              <button type="button" class="xq-btn" data-action="to-quote">Get my detailed quote</button></span></div>
              <p class="xq-hint xqf-quote-hint">Your detailed quote shows the exact system, fixed prices and running costs, straight away. It's subject to a home survey.</p>`
          : footer("to-contact", null, "Get my exact price")
      }`;
  }

  // ─── Validation and navigation ─────────────────────────────────────────────

  /** Required Property answers still missing, in page order (the EPC usually fills them). */
  function missingFields(): (keyof FullProperty)[] {
    const p = state.property;
    return [
      ...(!p.ageBand ? (["ageBand"] as const) : []),
      ...(!(p.storeys > 0) ? (["storeys"] as const) : []),
      ...(!(p.floorArea > 0) ? (["floorArea"] as const) : []),
    ];
  }

  function propertyComplete(): boolean {
    return missingFields().length === 0;
  }

  /** The pulsing star on the first missing answer ("answer this next"). */
  function needStar(show: boolean): string {
    return show
      ? '<span class="xqf-need" role="img" aria-label="Answer this to continue"><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 2.5l2.9 6.1 6.6.8-4.9 4.6 1.3 6.6L12 17.4l-5.9 3.2 1.3-6.6-4.9-4.6 6.6-.8z" fill="currentColor"/></svg></span>'
      : "";
  }

  /** Update the missing-answer glow in place while typing (no re-render, so focus stays). */
  function syncMissing() {
    if (state.step !== "property") return;
    const missing = missingFields();
    root.querySelectorAll<HTMLElement>("[data-row]").forEach((r) => {
      const f = r.dataset.row as keyof FullProperty;
      r.classList.toggle("is-missing", missing.includes(f));
      const star = r.querySelector(".xqf-need");
      if (star && f !== missing[0]) star.remove();
      if (!star && f === missing[0]) r.insertAdjacentHTML("afterbegin", needStar(true));
    });
    const note = root.querySelector<HTMLElement>("[data-missing-note]");
    if (note) {
      note.hidden = missing.length === 0;
      note.textContent = `Please answer the highlighted ${missing.length === 1 ? "question" : "questions"} to continue.`;
    }
  }

  function syncDisabled() {
    const valid: Record<string, boolean> = {
      postcode: POSTCODE_RE.test(state.postcode.trim()),
      address: state.address !== null,
      property: propertyComplete(),
      grant: grantAnswered() && !!state.grantResult && (!state.grantResult.eligible || state.includeGrant !== null),
      contact: state.name.trim().length > 1 && EMAIL_RE.test(state.email.trim()) && state.consent,
    };
    root.querySelectorAll<HTMLButtonElement>("[data-needs]").forEach((b) => {
      b.disabled = state.busy || !valid[b.dataset.needs!];
    });
    syncMissing();
  }

  function go(step: FullStep) {
    opts.onStep?.(step);
    if (step === "contact") api.track?.("contact");
    state.step = step;
    state.reached = Math.max(state.reached, FULL_STEPS.indexOf(step));
    state.error = null;
    render();
    root.querySelector(".xqf-main")?.scrollIntoView?.({ block: "nearest" });
  }

  // ─── Actions ───────────────────────────────────────────────────────────────

  async function findAddresses() {
    state.busy = true;
    state.error = null;
    state.addresses = [];
    render();
    try {
      state.addresses = await api.searchAddresses(state.postcode.trim());
      state.address = null;
      if (!state.addresses.length) state.error = "We couldn't find any addresses for that postcode.";
    } catch {
      state.error = "Something went wrong looking up your postcode. Please try again.";
    }
    state.busy = false;
    render();
  }

  /** Look up the chosen home's EPC and pre-fill the Property page from it. */
  async function applyLookup(address: Address) {
    try {
      state.lookup = await api.lookupProperty(address);
    } catch {
      state.lookup = null;
    }
    const fromEpc = Object.fromEntries(Object.entries(state.lookup?.full?.property ?? {}).filter(([, v]) => v !== null && v !== undefined)) as Partial<FullProperty>;
    // Bedrooms: the small widget's EPC estimate (habitable rooms − 1)
    if (state.lookup?.details.bedrooms) fromEpc.bedrooms = state.lookup.details.bedrooms;
    state.fromEpc = fromEpc;
    state.property = { ...BLANK, ...fromEpc };
    // Not-applicable roof/floor (flats) stay null so the question is hidden
    const full = state.lookup?.full?.property;
    if (full && full.roofInsulated === null) state.property.roofInsulated = null;
    if (full && full.floorInsulated === null) state.property.floorInsulated = null;
    if (fromEpc.mainsGas !== undefined) state.grantAnswers = { ...state.grantAnswers, mainsGas: fromEpc.mainsGas };
  }

  async function toEpc() {
    if (!state.address) return;
    if (state.lookup === undefined) {
      state.busy = true;
      render();
      await applyLookup(state.address);
      state.busy = false;
    }
    go("epc");
  }

  /** Re-check the grant whenever the answers are complete. */
  async function refreshGrant() {
    if (!grantAnswered() || !state.address) return;
    state.grantResult = null;
    render();
    try {
      state.grantResult = api.checkGrant
        ? await api.checkGrant(state.address, state.property, state.grantAnswers as GrantAnswers)
        : { grant: 0, uplift: false, eligible: false, checks: [], note: "" };
      if (!state.grantResult.eligible) state.includeGrant = false;
    } catch {
      state.error = "We couldn't check the grant right now. Please try again.";
    }
    render();
  }

  /** The Property answers and grant choice, as sent for a quote or an enquiry. */
  function fullRequest() {
    return { property: state.property, grantAnswers: state.grantAnswers as GrantAnswers, includeGrant: !!state.includeGrant && !!state.grantResult?.eligible };
  }

  async function calculate() {
    state.busy = true;
    render();
    try {
      state.quote = await api.getQuote(state.address!, toDetails(state.property), fullRequest());
      state.busy = false;
      go("estimate");
    } catch {
      state.busy = false;
      state.error = "We couldn't calculate an estimate right now. Please try again.";
      render();
    }
  }

  async function submitLead() {
    state.busy = true;
    render();
    try {
      const lead = {
        address: state.address!,
        details: toDetails(state.property),
        quote: state.quote!,
        name: state.name.trim(),
        email: state.email.trim(),
        phone: state.phone.trim(),
        consent: state.consent,
        full: fullRequest(),
      };
      if (state.wantsQuote && api.requestQuote) {
        // No quote (e.g. none of the installer's options fits): it went as a callback request
        state.quoteUrl = (await api.requestQuote(lead)).url;
      } else {
        await api.submitLead(lead);
      }
      state.busy = false;
      go("done");
    } catch {
      state.busy = false;
      state.error = "We couldn't send your details. Please try again.";
      render();
    }
  }

  /** Set a Property answer (numbers clamped; floor area stored in m²). */
  function setField(f: keyof FullProperty, raw: string | number | boolean) {
    const p = state.property as unknown as Record<string, unknown>;
    if (typeof raw === "boolean") p[f] = raw;
    else if (["storeys", "bedrooms", "bathrooms", "radiators", "floorArea"].includes(f)) {
      let n = Math.max(0, Math.round(Number(raw) || 0));
      if (f === "floorArea" && state.sqft) n = Math.round(n / SQFT_PER_M2);
      p[f] = n;
    } else p[f] = raw;
    // The grant depends on the fuel; answers may need re-checking
    if (f === "fuel") state.grantResult = null;
  }

  // ─── Events ────────────────────────────────────────────────────────────────

  function onClick(e: Event) {
    const el = (e.target as HTMLElement).closest<HTMLElement>("[data-action]");
    if (!el) return;
    const back: Partial<Record<FullStep, FullStep>> = { epc: "location", property: "epc", grant: "property", estimate: "grant", contact: "estimate" };
    const f = el.dataset.field as keyof FullProperty;
    switch (el.dataset.action) {
      case "open":
        state.open = true;
        api.track?.("opened");
        return render();
      case "close":
        state.open = false;
        return render();
      case "pick-address":
        state.address = state.addresses.find((a) => a.id === el.dataset.id) ?? null;
        state.lookup = undefined;
        state.grantResult = null;
        state.quote = null;
        state.reached = 0;
        root.querySelectorAll<HTMLElement>(".xq-option").forEach((b) => b.setAttribute("aria-pressed", String(b.dataset.id === state.address?.id)));
        return syncDisabled();
      case "to-epc":
        return void toEpc();
      case "to-property":
        return go("property");
      case "to-grant":
        go("grant");
        return void refreshGrant();
      case "to-estimate":
        return void calculate();
      case "to-contact":
        state.wantsQuote = false;
        return go("contact");
      case "to-quote":
        state.wantsQuote = true;
        return go("contact");
      case "goto":
        return go(el.dataset.step as FullStep);
      case "back":
        return go(back[state.step] ?? "location");
      case "step": {
        const cur = state.property[f] as number;
        const unitStep = f === "floorArea" ? (state.sqft ? Math.round(10 * SQFT_PER_M2) / SQFT_PER_M2 : 5) : 1;
        (state.property as unknown as Record<string, number>)[f] = Math.max(0, Math.round(cur + Number(el.dataset.delta) * unitStep));
        return render();
      }
      case "yesno":
        setField(f, el.dataset.value === "true");
        return render();
      case "units":
        state.sqft = !state.sqft;
        return render();
      case "grant":
        state.grantAnswers = { ...state.grantAnswers, [el.dataset.key!]: el.dataset.value === "true" };
        render();
        return void refreshGrant();
      case "include-grant":
        state.includeGrant = el.dataset.value === "true";
        return render();
      case "restart":
        Object.assign(state, {
          postcode: "", addresses: [], address: null, lookup: undefined, property: { ...BLANK }, fromEpc: {}, grantAnswers: {}, grantResult: null,
          includeGrant: null, quote: null, wantsQuote: false, quoteUrl: null, name: "", email: "", phone: "", consent: false, reached: 0,
        });
        return go("location");
    }
  }

  function onInput(e: Event) {
    const el = e.target as HTMLInputElement | HTMLSelectElement;
    if (el.dataset.field) {
      setField(el.dataset.field as keyof FullProperty, el.value);
      // Selects and finished number edits re-render (tags, hidden rows); typing doesn't, to keep focus
      if (e.type === "change") render();
      else syncDisabled();
      return;
    }
    const model = el.dataset.model;
    if (!model) return;
    if (model === "consent") state.consent = (el as HTMLInputElement).checked;
    else (state as unknown as Record<string, string>)[model] = el.value;
    syncDisabled();
  }

  function onSubmit(e: Event) {
    e.preventDefault();
    const form = (e.target as HTMLElement).dataset.form;
    if (form === "postcode" && POSTCODE_RE.test(state.postcode.trim())) void findAddresses();
    if (form === "contact") void submitLead();
  }

  shadow.addEventListener("click", onClick);
  shadow.addEventListener("input", onInput);
  shadow.addEventListener("change", onInput);
  shadow.addEventListener("submit", onSubmit);
  render();

  return {
    update(next) {
      branding = next;
      render();
    },

    /** Preview: show a step, filling earlier steps with sample data from the (mock) api. */
    async goTo(target) {
      const step = target as FullStep;
      const i = FULL_STEPS.indexOf(step);
      if (i < 0) return;
      if (i >= 1 && !state.addresses.length) {
        state.postcode = "SW1A 1AA";
        state.addresses = await api.searchAddresses(state.postcode);
      }
      if (i >= 1 && !state.address) state.address = state.addresses[0] ?? null;
      if (i >= 1 && state.lookup === undefined && state.address) await applyLookup(state.address);
      if (i >= 2 && !state.property.ageBand) state.property.ageBand = "D";
      if (i >= 2 && !state.property.floorArea) state.property.floorArea = 90;
      if (i >= 3 && !grantAnswered()) {
        state.grantAnswers = { owner: true, newBuild: false, previousFunding: false, mainsGas: state.grantAnswers.mainsGas ?? true, ...state.grantAnswers };
        await refreshGrant();
        if (state.includeGrant === null) state.includeGrant = !!state.grantResult?.eligible;
      }
      if (i >= 4 && !state.quote && state.address) {
        state.quote = await api.getQuote(state.address, toDetails(state.property), fullRequest());
      }
      if (i >= 5 && !state.name) Object.assign(state, { name: "Alex Morgan", email: "alex@example.com", consent: true });
      state.open = true;
      go(step);
    },

    destroy() {
      shadow.removeEventListener("click", onClick);
      shadow.removeEventListener("input", onInput);
      shadow.removeEventListener("change", onInput);
      shadow.removeEventListener("submit", onSubmit);
      shadow.innerHTML = "";
    },
  };
}
